import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';
import { useCallback, useMemo, useRef } from 'react';

import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { expVal } from '@confluence/feature-experiments';
import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	VIEW_PAGE,
	VIEW_BLOG,
	VIEW_BLOG_DATE_LEGACY,
} from '@confluence/named-routes/entry-points/routes.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { writeFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/writeFragmentInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { isExpectedError } from '../../utils/isExpectedError.ts';
import { type OnboardingKeysCombinedType } from '../../constants/onboarding-keys-combined.ts';
import { filterOnboardingState } from '../../utils/filterOnboardingState.ts';

import onboardingStateGetOnboardingStateQuery, {
	type onboardingStateGetOnboardingStateQuery$data,
	type onboardingStateGetOnboardingStateQuery$variables,
} from './__generated__/onboardingStateGetOnboardingStateQuery.graphql.ts';
import type {
	OnboardingStateInput,
	onboardingStateSetOnboardingStateMutation as onboardingStateSetOnboardingStateMutation$data,
	onboardingStateSetOnboardingStateMutationVariables as onboardingStateSetOnboardingStateMutation$variables,
} from './__types__/onboardingStateSetOnboardingStateMutation.ts';

// PersonalizationContract fragment baseline. Colocated here so the onboarding key/value pair is
// owned by its consumer, then spread by whichever operation supplies it.
//
// Unlike the other field groups in this contract, this fragment is NOT spread into the legacy
// operation: `onboardingStateGetOnboardingStateQuery` has no source `graphql` tag anywhere in
// confluence/ — only a checked-in generated artifact that 48 files import directly — so there is no
// operation source to add a spread to. Its first spread is therefore in
// ViewPagePersonalizationQuery. See confluence/openspec/changes/personalization-contract.
export const OnboardingStateFragment: GraphQLTaggedNode = graphql`
	fragment onboardingState_state on OnboardingState {
		key
		value
	}
`;

// Relay generated types are immutable (readonly) whereas Apollo expects mutable types
type OnboardingStateData = {
	onboardingState?:
		| readonly {
				key: string;
				value: string | null | undefined;
		  }[]
		| null;
};

const isOnboardingStateEqual = (
	a:
		| readonly {
				key: string;
				value: string | null | undefined;
		  }[]
		| null
		| undefined,
	b:
		| readonly {
				key: string;
				value: string | null | undefined;
		  }[]
		| null
		| undefined,
): boolean => {
	if (a === b) return true;
	if (!a || !b) return !a && !b;
	if (a.length !== b.length) return false;
	return a.every((item, i) => item.key === b[i].key && item.value === b[i].value);
};

// Hook to check if we're on a view page route
const useIsViewPageRoute = (): boolean =>
	useRouteName({
		selector: (routeName: string | undefined) =>
			routeName === VIEW_PAGE.name ||
			routeName === VIEW_BLOG.name ||
			routeName === VIEW_BLOG_DATE_LEGACY.name,
	});

// Get the combined onboarding state from cache, returns null if not cached
const getCombinedQueryFromCache = (): onboardingStateGetOnboardingStateQuery$data | null => {
	try {
		return readQueryInterop<
			onboardingStateGetOnboardingStateQuery$data,
			onboardingStateGetOnboardingStateQuery$variables
		>({
			query: onboardingStateGetOnboardingStateQuery,
			variables: { key: [] },
		});
	} catch {
		return null;
	}
};

// Updates the combined query cache with new onboarding state values.
// This ensures components using the centralized cache see updates on next mount/navigation.
const updateCombinedQueryCache = (
	updatedStates: readonly {
		key: string;
		value: string | null | undefined;
	}[],
): void => {
	if (!expVal('confluence_centralized_onboarding_state', 'isEnabled', false)) {
		return;
	}

	const currentData = getCombinedQueryFromCache();
	if (!currentData?.onboardingState) {
		return;
	}

	const updatedMap = new Map(updatedStates.map((s) => [s.key, s.value]));

	const newOnboardingState = currentData.onboardingState.map((item) =>
		updatedMap.has(item.key) ? { ...item, value: updatedMap.get(item.key) ?? item.value } : item,
	);

	writeQueryInterop({
		query: onboardingStateGetOnboardingStateQuery,
		variables: { key: [] },
		data: { onboardingState: newOnboardingState },
	});
};

export const deserializeState = (
	data: onboardingStateGetOnboardingStateQuery$data | OnboardingStateData | null | undefined,
): any => {
	const state = {} as any;
	// new graphQL call returns onboardingState array
	const { onboardingState } = data || {};

	// parse data and populate state accordingly
	if (onboardingState !== undefined && onboardingState !== null) {
		for (const { key, value } of onboardingState) {
			// key comes back as "{prefix}:{property}" format
			// split by ':' to get parsed {property}
			// some keys don't have a prefix, which is why we have the ||
			const parsedKey = key.split(':')[1] || key;
			if (!value) {
				continue;
			}
			if (/(true|false)/g.test(value)) {
				state[parsedKey] = value === 'true';
			} else {
				state[parsedKey] = value;
			}
		}
	}
	return state;
};

export const useSetOnboardingState = (): {
	setOnboardingState: (newOnboardingState: OnboardingStateInput | OnboardingStateInput[]) => void;
	error: ApolloError | undefined;
	called: boolean;
} => {
	const [setState, { error, called }] = useMutationInterop<
		onboardingStateSetOnboardingStateMutation$data,
		onboardingStateSetOnboardingStateMutation$variables
	>(
		graphql`
			mutation onboardingStateSetOnboardingStateMutation($states: [OnboardingStateInput!]!) {
				setOnboardingState(states: $states) {
					key
					value
				}
			}
		`,
		{
			update(_cache, { data }) {
				const states = data!.setOnboardingState;
				(states ?? []).forEach(({ key, value }) => {
					// writeFragment can only handle a single data item at a time
					writeFragmentInterop({
						fragmentId: `OnboardingState:${key}`,
						data: {
							key,
							value: value ?? null,
							__typename: 'OnboardingState',
						},
					});
				});

				// Update the combined query cache for centralized onboarding state
				updateCombinedQueryCache(states ?? []);
			},
			onError: (error) => {
				if (isExpectedError(error)) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	type SetOnboardingStateArgs = OnboardingStateInput | OnboardingStateInput[];

	const setOnboardingState = useCallback(
		(newOnboardingState: SetOnboardingStateArgs): void => {
			let states: OnboardingStateInput[];
			if (Array.isArray(newOnboardingState)) {
				// supports batch updates
				states = newOnboardingState;
			} else {
				// backwards compability for single key-value pair
				states = [newOnboardingState];
			}

			void setState({
				variables: {
					states,
				},
				optimisticResponse: {
					setOnboardingState: states.map(({ key, value }) => ({
						key,
						value,
						__typename: 'OnboardingState',
					})),
				},
			});
		},
		[setState],
	);

	return { setOnboardingState, error, called };
};

// safeguards against empty arrays or strings
const hasValidKeysToFetch = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
): boolean => {
	if (Array.isArray(key)) return key.length > 0;
	if (typeof key === 'string') return key.length > 0;
	return false;
};

/**
 * TODO: Migrate to fetch from userPreferences - this is considered deprecated
 *
 * @param key - The onboarding state key(s) to fetch
 * @param skip - Whether to skip the query
 * @param reactive - When true (default), subscribes to centralized cache changes for immediate updates when
 * setOnboardingState is called. No additional network requests are made.
 * When false: reads from centralized cache once, sees updates on next navigation/mount.
 */
export const useGetOnboardingState = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
	skip = false,
	reactive = true,
): {
	data: onboardingStateGetOnboardingStateQuery$data | undefined;
	error: ApolloErrorInterop | undefined;
	loading: boolean;
	refetch: RefetchSupportedType<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>;
} => {
	const { isLicensed, isGuest } = useAccessStatus();
	const isAllowed = isLicensed || isGuest;
	const isViewPageRoute = useIsViewPageRoute();

	const hasKeysToFetch = hasValidKeysToFetch(key);
	const skipQuery = skip || !isAllowed || !hasKeysToFetch;

	// Check if centralized cache should be used (experiment enabled + on view page route)
	const useCentralizedCache =
		isViewPageRoute && expVal('confluence_centralized_onboarding_state', 'isEnabled', false);

	// For non-reactive mode: read from cache once (no subscription)
	// This is a one-time synchronous read that doesn't trigger re-renders on cache changes
	const cachedCombinedData = !reactive && useCentralizedCache ? getCombinedQueryFromCache() : null;
	const hasCachedCombinedData = cachedCombinedData !== null;

	// For reactive mode: use useQueryInterop with cache-only to subscribe to cache changes
	// This subscribes to the centralized cache and re-renders when it changes (no network requests)
	const {
		data: reactiveCacheData,
		error: reactiveCacheError,
		loading: reactiveCacheLoading,
	} = useQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(onboardingStateGetOnboardingStateQuery, {
		variables: { key: [] },
		skip: skipQuery || !reactive || !useCentralizedCache,
		fetchPolicy: 'cache-only',
	});

	// Memoize the filtered reactive cache result so filterOnboardingState is only called
	// when reactiveCacheData or key actually changes — not on every render.
	//
	// We must not skip the fallback just because reactiveCacheData is truthy — the centralized
	// cache may not contain the specific keys this consumer requested (e.g. the preload fetched
	// a different set of keys). We only skip the fallback when the filtered reactive cache data
	// actually has entries for the requested keys.
	const { reactiveFilteredData, reactiveFilteredHasData } = useMemo(() => {
		if (!(reactive && useCentralizedCache && reactiveCacheData)) {
			return { reactiveFilteredData: null, reactiveFilteredHasData: false };
		}
		const filtered = filterOnboardingState(reactiveCacheData, key);
		return {
			reactiveFilteredData: filtered,
			reactiveFilteredHasData: (filtered?.onboardingState?.length ?? 0) > 0,
		};
	}, [reactive, useCentralizedCache, reactiveCacheData, key]);

	const {
		data: fallbackQueryData,
		error: fallbackError,
		loading: fallbackLoading,
		refetch,
	} = useQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(onboardingStateGetOnboardingStateQuery, {
		variables: { key: key as unknown as string[] },
		skip: skipQuery || hasCachedCombinedData || reactiveFilteredHasData,
	});

	// Determine which data source to use:
	// 1. Non-reactive with cached data: use cachedCombinedData (filtered)
	// 2. Reactive with centralized cache and keys found: use reactiveFilteredData
	// 3. Fallback: use fallbackQueryData
	// For reactive path: skip re-render if filtered data for our keys hasn't changed
	const prevFilteredDataRef = useRef<onboardingStateGetOnboardingStateQuery$data | undefined>(
		undefined,
	);
	const data = useMemo(() => {
		if (hasCachedCombinedData) {
			return filterOnboardingState(cachedCombinedData, key);
		}
		if (reactiveFilteredHasData && reactiveFilteredData) {
			if (
				prevFilteredDataRef.current &&
				isOnboardingStateEqual(
					prevFilteredDataRef.current.onboardingState,
					reactiveFilteredData.onboardingState,
				)
			) {
				return prevFilteredDataRef.current;
			}
			prevFilteredDataRef.current = reactiveFilteredData;
			return reactiveFilteredData;
		}
		return fallbackQueryData;
	}, [
		hasCachedCombinedData,
		cachedCombinedData,
		reactiveFilteredHasData,
		reactiveFilteredData,
		fallbackQueryData,
		key,
	]);

	// Combine error and loading states from both queries
	const error = reactiveCacheError || fallbackError;

	// When centralized cache is enabled in reactive mode but the preload hasn't populated
	// the cache yet, cache-only returns loading: false with data: undefined. Without this
	// guard, consumers interpret undefined data as "user never saw this onboarding" and
	// incorrectly show spotlights/changeboarding that was already dismissed.
	// We report loading: true until either the reactive cache or fallback has actual data.
	//
	// Also guard the case where the reactive cache is populated but doesn't contain the
	// specific keys this consumer requested (reactiveFilteredHasData is false). In that
	// scenario the fallback query has been allowed to run — keep loading: true until it
	// either returns data or an error, so consumers don't prematurely see empty state.
	const isCentralizedCacheWaitingForPreload =
		reactive &&
		useCentralizedCache &&
		(!reactiveCacheData || !reactiveFilteredHasData) &&
		!fallbackQueryData &&
		!skipQuery &&
		!reactiveCacheError &&
		!fallbackError;
	const loading =
		isCentralizedCacheWaitingForPreload ||
		(reactive && useCentralizedCache ? reactiveCacheLoading : false) ||
		fallbackLoading;

	return {
		data,
		error,
		loading,
		refetch,
	};
};
